import React, {useCallback, useEffect, useRef, useState} from "react";
import {IconButton, Tooltip, Typography} from "@mui/material";
import {Check, ContentCopy} from "@mui/icons-material";
import {Grey5, Grey17} from "../../theme/styles";

/**
 * A value worth retyping, with the retyping removed.
 *
 * An object identifier -- SOP-00194 -- is the thing people carry OUT of this screen: into an email,
 * a change request, a conversation with an auditor. Reading eight characters off a screen and typing
 * them somewhere else is exactly the transcription a quality system is supposed to eliminate, and it
 * is the kind of copy that is wrong one time in fifty and silently.
 *
 * THE ICON IS ALWAYS THERE, not revealed on hover. A control nobody can see is a control nobody
 * uses, and hover reveals do not exist for a reader on a touch screen at all.
 *
 * IT SAYS WHEN IT WORKED. The clipboard gives no feedback of its own, so a click that appears to do
 * nothing is indistinguishable from a click that failed -- and the reader's next move is to paste
 * somewhere and find out. The icon becomes a tick for a moment, and the tooltip says so.
 *
 * AND WHEN IT DID NOT. writeText rejects on a page the browser does not consider secure, and on a
 * document that does not have focus. Reporting that is the difference between "this button is
 * broken" and "select it and press the shortcut" -- so the failure is shown rather than swallowed.
 */
export default function CopyableValue({text, label = "Copy"}: {text: any; label?: string}) {
    const [state, setState] = useState<"idle" | "copied" | "failed">("idle");
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

    // The timeout outlives the component if the reader navigates away in the second after copying,
    // and setting state on an unmounted component is a warning at best.
    useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

    const copy = useCallback(async (event: React.MouseEvent) => {
        // The value often sits inside something clickable -- a row, a card. Copying is not navigating.
        event.stopPropagation();
        event.preventDefault();

        try {
            await navigator.clipboard.writeText(String(text ?? ""));
            setState("copied");
        } catch {
            setState("failed");
        }
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => setState("idle"), 1600);
    }, [text]);

    // Nothing to copy is not a disabled button -- it is no button. An icon that cannot do anything
    // still asks to be clicked.
    if (text === null || text === undefined || text === "") {
        return <Typography sx={{fontSize: 15, color: Grey17, lineHeight: 1.45}}>—</Typography>;
    }

    const title = state === "copied" ? "Copied"
                : state === "failed" ? "Could not copy — select the text and copy it yourself"
                : label;

    return (
        <span style={{display: "inline-flex", alignItems: "center", gap: 2, minWidth: 0}}>
            <Typography sx={{fontSize: 15, fontWeight: 400, color: Grey17, lineHeight: 1.45,
                             overflowWrap: "anywhere"}}>
                {text}
            </Typography>
            <Tooltip title={title} placement="top">
                {/* The button keeps its own accessible name, because the icon alone says nothing to
                    a screen reader and "button" is not an instruction. */}
                <IconButton onClick={copy} size="small" aria-label={`${label} ${text}`}
                            sx={{padding: "2px", color: state === "copied" ? "#2E7D32" : Grey5,
                                 "&:hover": {backgroundColor: "rgba(0,0,0,0.04)"}}}>
                    {state === "copied"
                        ? <Check sx={{fontSize: 15}}/>
                        : <ContentCopy sx={{fontSize: 14}}/>}
                </IconButton>
            </Tooltip>
        </span>
    );
}
